import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaHome, FaBox, FaTh, FaShoppingCart, FaCalendar, FaStar, FaStore, FaBook, 
  FaCheckCircle, FaUsers, FaBullhorn, FaClipboardCheck, FaComments, 
  FaClipboardList, FaKey, FaTimes, FaShieldAlt, FaCrown, FaListAlt, 
  FaUserCheck, FaChevronDown, FaChevronRight, FaExchangeAlt, FaFunnelDollar,
  FaFileUpload, FaGavel
} from 'react-icons/fa';

export default function AdminNav({ onClose, isMobile = false }) {
  const { t } = useTranslation();
  const location = useLocation();

  // Menu organisé par catégories
  const menuSections = [
    {
      title: null, // Pas de titre pour le dashboard
      items: [
        { path: '/admin', icon: FaHome, label: 'Tableau de Bord', exact: true },
        { path: '/admin/stats', icon: FaBullhorn, label: 'Statistiques & KPIs' },
      ]
    },
    {
      title: '👥 Utilisateurs',
      items: [
        { path: '/admin/users', icon: FaUsers, label: 'Tous les utilisateurs' },
        { path: '/admin/user-approvals', icon: FaUserCheck, label: 'Approbations', badge: true },
      ]
    },
    {
      title: '🔒 Sécurité',
      items: [
        { path: '/admin/security', icon: FaShieldAlt, label: 'Anti-Fraude' },
      ]
    },
    {
      title: '🔨 Enchères',
      items: [
        { path: '/admin/encheres', icon: FaGavel, label: 'Gestion Enchères', badge: true },
      ]
    },
    {
      title: '🚀 Pipeline Leads',
      items: [
        { path: '/admin/demandes', icon: FaFunnelDollar, label: 'Toutes les demandes', badge: true },
      ]
    },
    {
      title: '📦 Annonces',
      items: [
        { path: '/admin/annonces', icon: FaListAlt, label: 'Gestion complète', badge: true },
        { path: '/admin/products', icon: FaBox, label: 'Produits (ancien)' },
        { path: '/admin/categories', icon: FaTh, label: 'Catégories' },
        { path: '/admin/templates', icon: FaClipboardList, label: 'Templates fiches' },
        { path: '/admin/comparator', icon: FaExchangeAlt, label: 'Comparateur' },
        { path: '/admin/product-approvals', icon: FaClipboardCheck, label: 'Approbations', badge: true },
      ]
    },
    {
      title: '👷 Talents & RH',
      items: [
        { path: '/admin/talents', icon: FaUsers, label: 'Annonces Talents' },
      ]
    },
    {
      title: '🔑 Locations',
      items: [
        { path: '/admin/rentals', icon: FaCalendar, label: 'Gestion' },
        { path: '/admin/rental-approvals', icon: FaKey, label: 'Approbations', badge: true },
        { path: '/admin/rental-messages', icon: FaComments, label: 'Messages' },
      ]
    },
    {
      title: '📋 Demandes',
      items: [
        { path: '/admin/requests-management', icon: FaListAlt, label: 'Gestion' },
        { path: '/admin/request-approvals', icon: FaClipboardList, label: 'Approbations' },
        { path: '/admin/quote-requests', icon: FaComments, label: 'Devis' },
      ]
    },
    {
      title: '📒 Annuaire',
      items: [
        { path: '/admin/directory', icon: FaBook, label: 'Vue générale' },
        { path: '/admin/directory-management', icon: FaStore, label: 'Gestion' },
        { path: '/admin/directory-import', icon: FaFileUpload, label: 'Import CSV' },
        { path: '/admin/directory-pending', icon: FaClipboardCheck, label: 'En attente', badge: true },
        { path: '/admin/directory-categories', icon: FaTh, label: 'Catégories' },
      ]
    },
    {
      title: '🛡️ Modération',
      items: [
        { path: '/admin/moderation', icon: FaCheckCircle, label: 'Contenus' },
        { path: '/admin/content-violations', icon: FaShieldAlt, label: 'Signalements' },
        { path: '/admin/reviews', icon: FaStar, label: 'Avis' },
      ]
    },
    {
      title: '💼 Business',
      items: [
        { path: '/admin/orders', icon: FaShoppingCart, label: 'Commandes' },
        { path: '/admin/subscriptions', icon: FaCrown, label: 'Abonnements' },
        { path: '/admin/banners', icon: FaBullhorn, label: 'Publicités' },
        { path: '/admin/shops', icon: FaStore, label: 'Boutiques' },
      ]
    },
  ];

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderMenuItems = (items, compact = false) => {
    return items.map((item) => {
      const Icon = item.icon;
      const active = isActive(item.path, item.exact);
      
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={onClose}
          className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all text-sm ${
            active
              ? 'bg-[#635BFF] text-white'
              : 'text-gray-600 hover:bg-gray-100 hover:text-[#0A2540]'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Icon className={`text-sm ${active ? 'text-white' : 'text-[#635BFF]'}`} />
            <span className={compact ? 'text-xs' : 'text-sm'}>{item.label}</span>
          </div>
          {item.badge && !active && (
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          )}
        </Link>
      );
    });
  };

  // Mobile version
  if (isMobile) {
    return (
      <div className="w-72 max-w-[85vw] bg-white shadow-xl h-full overflow-y-auto" data-testid="admin-nav-mobile">
        {/* Mobile Header */}
        <div className="sticky top-0 bg-[#0A2540] p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-white">Admin Panel</h2>
            <p className="text-xs text-gray-300">GEO TS CONSULTING</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[#003d56] rounded-lg text-white"
            aria-label="Fermer le menu"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Navigation sections */}
        <nav className="p-3">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={section.title ? 'mt-4' : ''}>
              {section.title && (
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">
                  {section.title}
                </h3>
              )}
              <div className="space-y-1">
                {renderMenuItems(section.items, true)}
              </div>
            </div>
          ))}
        </nav>

        {/* Back to site */}
        <div className="p-3 border-t mt-4">
          <Link 
            to="/" 
            onClick={onClose} 
            className="text-sm text-gray-500 hover:text-[#0A2540] flex items-center space-x-2 px-3 py-2"
          >
            <span>←</span>
            <span>Retour au site</span>
          </Link>
        </div>
      </div>
    );
  }

  // Desktop version
  return (
    <div className="w-64 bg-white shadow-lg min-h-screen overflow-y-auto" data-testid="admin-nav">
      {/* Header */}
      <div className="p-4 border-b bg-[#0A2540]">
        <h2 className="text-lg font-bold text-white">Admin Panel</h2>
        <p className="text-xs text-gray-300">GEO TS CONSULTING</p>
      </div>

      {/* Navigation sections */}
      <nav className="p-3">
        {menuSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={section.title ? 'mt-4' : ''}>
            {section.title && (
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {renderMenuItems(section.items)}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t mt-4">
        <Link 
          to="/" 
          onClick={onClose} 
          className="text-sm text-gray-500 hover:text-[#0A2540] flex items-center space-x-2 px-3 py-2"
        >
          <span>←</span>
          <span>Retour au site</span>
        </Link>
      </div>
    </div>
  );
}
